<template>
  <div>
    <h1 class="page-title">
      <span>Mains Outage</span>
    </h1>

    <b-tabs>
      <b-tab active>
        <template v-slot:title>
          <span class="tab-title">
            Events
            <b-badge variant="primary">{{ events.length }}</b-badge>
          </span>
        </template>

        <div class="toolbar">
          <div class="d-flex align-items-center gap-2">
            <small class="text-muted">
                Refresh: 
            </small>

            <b-form-select
              v-model="refreshInterval"
              :options="refreshOptions"
              size="sm"
              style="width: 100px;"
              @change="onRefreshIntervalChange"
            />
          </div>
        </div>
        <ResponsiveTable
          :fields="fields"
          :items="events"
          :loading="loading"
          sort-by="event_time"
          sort-direction="desc"
        >
          <!-- EVENT TYPE -->
          <template v-slot:cell(event_type)="row">
            <b-badge
              :variant="
                row.item.event_type === 'MAINS_NO_VOLTAGE'
                  ? 'danger'
                  : 'success'
              "
            >
              {{ row.item.event_type }}
            </b-badge>
          </template>

          <!-- DATES -->
          <template v-slot:cell(event_time)="row">
            {{ row.item.event_time | formatDate }}
          </template>

          <template v-slot:cell(received_time)="row">
            {{ row.item.received_time | formatDate }}
          </template>

          <!-- CIRCUIT -->
          <template v-slot:cell(circuit)="row">
            <span>{{ row.item.circuit }}</span>
          </template>

          <!-- SUBSTATION -->
          <template v-slot:cell(substation)="row">
            {{ row.item.substation }}
          </template>

          <!-- SERIAL -->
          <template v-slot:cell(serial)="row">
            <router-link
              :to="{
                name: 'DevicePage',
                params: {
                  serial: row.item.serial,
                  fromDevicePage: true
                }
              }"
            >
              <span class="table-link">
                {{ row.item.serial }}
              </span>
            </router-link>
          </template>
        </ResponsiveTable>
      </b-tab>
    </b-tabs>
  </div>
</template>

<script>
import { diagnosticService, deviceService } from "@/_services";
import ResponsiveTable from "@/components/ResponsiveTable/ResponsiveTable";
import { mapState } from "vuex";

export default {
  name: "MainsOutage",

  components: {
    ResponsiveTable,
  },

  data() {
    return {
      devices: [],

      refreshOptions: [
        { value: 0, text: "Off" },
        { value: 10000, text: "10s" },
        { value: 30000, text: "30s" },
        { value: 60000, text: "1 min" },
        { value: 300000, text: "5 min" },
      ],

      refreshInterval: 60000,
      refreshTimer: null,

      fields: [
        { key: "event_type", label: "EVENT TYPE", sortable: true },
        { key: "event_time", label: "EVENT TIME", sortable: true },
        { key: "received_time", label: "RECEIVED TIME", sortable: true },
        { key: "circuit", label: "CIRCUIT", sortable: true },
        { key: "substation", label: "SUBSTATION", sortable: true },
        { key: "serial", label: "SERIAL", sortable: true },
      ],

      events: [],
      loading: false
    };
  },

  computed: {
    ...mapState("layout", {
      hideInactiveCircuits: (state) => state.hideInactiveCircuits,
    }),

    deviceMap() {
      const map = {};
      this.devices.forEach((d) => {
        if (d.product_serial_num) {
          map[d.product_serial_num] = d;
        }
      });
      return map;
    },
  },

  methods: {
    fetchEvents() {
      
      this.loading = true;

      return diagnosticService.getMainsSupplyEvents()
        .then((response) => {
          let events = response.data;

          if (typeof events === "string") {
            events = JSON.parse(events);
          }
          const allowedTypes = ["MAINS_NO_VOLTAGE", "MAINS_OK"];
          events = events.filter((e) =>
            allowedTypes.includes(e.event_type)
          );

          this.events = events.map((e) => {
            const serialKey = e.product_serial_num || e.device_serial;
            const device = this.deviceMap[serialKey];

            return {
              serial: serialKey || "",
              circuit:
               device && device.pole && device.pole.circuit_name
                  ? device.pole.circuit_name
                  : "",

              substation: device && device.pole && device.pole.pole_substation_name
                  ? device.pole.pole_substation_name
                  : "",

              event_time: e.event_time,
              received_time: e.received_time,
              event_type: e.event_type,
            };
          });
        })
        .catch((error) => {
          console.error("Error fetching events:", error);
        })
        .finally(() => {
          this.loading = false;
        });

    },

    startRefreshTimer() {
      if (this.refreshTimer) {
        clearInterval(this.refreshTimer);
        this.refreshTimer = null;
      }

      if (!this.refreshInterval || this.refreshInterval === 0) return;

      this.refreshTimer = setInterval(() => {
        this.refreshData();
      }, this.refreshInterval);
    },

    onRefreshIntervalChange() {
      if (this.refreshInterval > 0) {
        this.refreshData();
      }
      this.startRefreshTimer();
    },

    getDevices() {
      return deviceService
        .findAll(this.hideInactiveCircuits)
        .then((response) => {
          this.devices = response.data.devices || [];
        })
        .catch((err) => {
          console.error("Error fetching devices:", err);
        });
    },

    refreshData() {
      this.fetchEvents();
    },
  },

  mounted() {
    this.getDevices().then(() => {
      this.fetchEvents();
      this.startRefreshTimer();
    });
  },

  beforeDestroy() {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
    }
  },
};
</script>

<style scoped>
.page-title {
  margin-bottom: 20px;
}

.toolbar {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 15px;
}

.table-link {
  color: #007bff;
  cursor: pointer;
}

.table-link:hover {
  text-decoration: underline;
}

.text-muted {
  margin-right: 5px;
}

.gap-2 > * {
  margin-left: 8px;
}



::v-deep .nav-tabs {
  background: transparent !important;
}

::v-deep .nav-tabs .nav-link.active {
  background: #fff !important;
}

.tab-title {
  margin: 0;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
}



</style>